// Emergencies this operator has already answered, remembered across a reload.
//
// Decline does NOT resolve an alarm — it records "not me" and leaves the emergency open, on the map
// and in the inbox, for whoever does take it. That is right. What was wrong is that the set of
// "I have already answered this" lived only in module memory, so a refresh forgot it and the
// still-open alarm was put to the same operator again, and again on the next refresh.
//
// From the operator's side that is the console badgering them about a decision they already made.
// Worse, it trains them to dismiss the surface that exists to be impossible to dismiss.
//
// So a decline survives the reload. Deliberately per BROWSER (`localStorage`), not per tab: "not
// me" is a statement about the person, and duplicating a tab is not a new answer.
//
// TWO things keep this from hiding a real emergency. It is keyed on the ALERT ID, so a device
// raising a NEW alarm always interrupts — a wearer who presses SOS again after being declined is a
// new emergency and is treated as one. And entries EXPIRE: an alarm nobody has resolved in a day is
// not one this operator's old decline should keep suppressing.

const KEY = 'gisify.sos.answered.v1'

/**
 * How long an answer suppresses its own alarm.
 *
 * A WEEK, raised from twelve hours, and the twelve hours were a real bug rather than a conservative
 * choice: an adversarial review of the emergency surface traced the reported symptom — an operator
 * confronted by a TWO-DAY-OLD emergency card — straight to this constant. The alarm had been
 * declined. Declining does not resolve it, so it stays open forever (a critical alert is never
 * auto-resolved, by design), the TTL lapsed after twelve hours, and the backlog replay put it back.
 * The operator's answer was being forgotten while the thing it answered was not.
 *
 * A week is long enough that no plausible shift pattern re-asks, and it stays bounded rather than
 * infinite so that an alarm genuinely still open next week reaches whoever is on duty then. It is
 * SAFE to make it this long only because a suppressed alert is not a hidden one: it keeps its row
 * in the inbox, its count in the bell and its pin on the map, with every action it had.
 */
export const ANSWERED_TTL_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Cap on remembered answers.
 *
 * `localStorage` is a shared, smallish quota and this is the least important thing in it. Oldest
 * out first.
 */
export const ANSWERED_MAX = 200

type Entry = { id: string; at: number }

/**
 * Every accessor is guarded, and the guard is around the ACCESSOR rather than the call: private
 * Safari throws on `window.localStorage` itself, before `getItem` is reached. A forgotten answer is
 * a nuisance; an emergency surface that fails to render is not.
 */
function read(): Entry[] {
  try {
    const raw = window.localStorage.getItem(KEY)

    if (!raw) return []

    const parsed: unknown = JSON.parse(raw)

    if (!Array.isArray(parsed)) return []

    // Validated field by field on READ. This is shared, user-writable storage, and a malformed
    // entry that slipped through would suppress an emergency — the one failure mode here that
    // actually costs something.
    return parsed.filter(
      (e): e is Entry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as Entry).id === 'string' &&
        typeof (e as Entry).at === 'number',
    )
  } catch {
    return []
  }
}

function write(entries: Entry[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(entries))
  } catch {
    // Quota, or a private window. The session's in-memory set still works for this page load.
  }
}

/** Alert ids answered recently enough to still count. Expired entries are dropped as a side effect. */
export function readAnsweredAlerts(now: number = Date.now()): string[] {
  const fresh = read().filter(e => now - e.at < ANSWERED_TTL_MS)

  return fresh.map(e => e.id)
}

/** Remember that this operator answered `alertId`. Idempotent. */
export function rememberAnsweredAlert(alertId: string, now: number = Date.now()): void {
  const fresh = read().filter(e => e.id !== alertId && now - e.at < ANSWERED_TTL_MS)

  fresh.push({ id: alertId, at: now })
  write(fresh.slice(-ANSWERED_MAX))
}

/** Forget an answer the server refused: the alarm was never answered, so it must be asked again. */
export function forgetAnsweredAlert(alertId: string, now: number = Date.now()): void {
  write(read().filter(e => e.id !== alertId && now - e.at < ANSWERED_TTL_MS))
}

/** Test seam, and the operator's escape hatch if one is ever needed. */
export function clearAnsweredAlerts(): void {
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // Same reasoning.
  }
}
